/** Revenue (super admin) — paid orders across every tenant. */
import { useEffect, useState } from "react";
import * as api from "../../api/billing";
import { Card, EmptyState, Spinner } from "../../components/ui";
import { useT } from "../../i18n";
import { formatMinor } from "./BillingPage";

export function RevenuePage() {
  const t = useT();
  const [summary, setSummary] = useState<api.RevenueSummary | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .revenueSummary()
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (failed) {
    return (
      <Card>
        <p role="alert" className="text-sm text-danger">{t("common.error.generic")}</p>
      </Card>
    );
  }
  if (!summary) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold">{t("revenue.title")}</h1>
        <p className="mt-1 text-muted">{t("revenue.subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <p className="text-xs uppercase text-muted">{t("revenue.total")}</p>
          <p className="text-3xl font-extrabold text-ink" data-testid="revenue-total">
            {formatMinor(summary.total_paid_minor, summary.currency)}
          </p>
        </Card>
        <Card>
          <p className="text-xs uppercase text-muted">{t("revenue.paidOrders")}</p>
          <p className="text-3xl font-extrabold text-ink">{summary.orders_paid}</p>
        </Card>
      </div>

      <Card title={t("billing.orders")}>
        {summary.orders.length === 0 ? (
          <EmptyState message={t("revenue.empty")} />
        ) : (
          <div className="min-w-0 overflow-x-auto">
            <table className="w-full min-w-[30rem] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-muted">
                  <th className="py-1">{t("billing.date")}</th>
                  <th>{t("revenue.tenant")}</th>
                  <th>{t("billing.plan")}</th>
                  <th>{t("billing.amount")}</th>
                </tr>
              </thead>
              <tbody>
                {summary.orders.map((order) => (
                  <tr key={order.id} className="border-t border-line">
                    <td className="py-2">
                      {order.paid_at ? new Date(order.paid_at).toLocaleDateString() : "—"}
                    </td>
                    <td>{order.tenant}</td>
                    <td>
                      {order.plan_code} · {order.period}
                    </td>
                    <td>{formatMinor(order.amount_minor, order.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
